/**
 * ...
 * @author Robert Lombardo, Wakefield Studios ( rob@wakefield-studios.com )
 */
const Q_how_many_nearby_enemies_BASE = require('../questions/Q_how_many_nearby_enemies_BASE');
const Q_can_i_just_kill_it = require('./Q_can_i_just_kill_it');
const Q_ranged_cluster = require('./Q_ranged_cluster');
const Q_which_aoe = require('./Q_which_aoe');
const BattleCalc = require('../../BattleCalc');

const CC_CONDITIONS = ['enraged', 'scared', 'slowed', 'stunned'];

const InterferenceDecision = function (actor, battleState) {
  const node = new Q_how_many_nearby_enemies_BASE(actor, battleState);

  // branch indexes
  node.ONE = 1;
  node.TWO_OR_MORE = 2;

  const iceNine = BattleCalc.getEquippedAbility(actor, 'ice_nine');
  const skyHammer = BattleCalc.getEquippedAbility(actor, 'sky_hammer');
  const devilMask = BattleCalc.getEquippedAbility(actor, 'devil_mask');
  const bluntArrow = BattleCalc.getEquippedAbility(actor, 'blunt_arrow');
  const legShot = BattleCalc.getEquippedAbility(actor, 'leg_shot');
  const slam = BattleCalc.getEquippedAbility(actor, 'slam');
  const sleightOfHand = BattleCalc.getEquippedAbility(actor, 'sleight_of_hand');
  node.defineNearby = bluntArrow
    ? bluntArrow.range.max
    : legShot
    ? legShot.range.max
    : sleightOfHand
    ? sleightOfHand.range.max
    : iceNine
    ? iceNine.range.max
    : skyHammer
    ? skyHammer.range.max
    : devilMask
    ? devilMask.effectRange
    : slam
    ? slam.range.max
    : 0;

  node.branches[node.ONE] = new Q_can_i_just_kill_it(actor, battleState);
  node.branches[node.ZERO] = new Q_ranged_cluster(
    node.branches[node.ONE],
    actor,
    battleState
  );
  node.branches[node.TWO_OR_MORE] = new Q_which_aoe(
    node.branches[node.ONE],
    actor,
    battleState
  );

  node.query = function () {
    const bestTarget = this.calculateBestInterferenceTarget();
    if (!bestTarget) {
      return null;
    }

    const numNearby = this.calculateNearbyEnemies(this.defineNearby);
    if (numNearby === 0) {
      return this.branches[this.ZERO].query(bestTarget);
    } else if (numNearby === 1) {
      return this.branches[this.ONE].query(bestTarget);
    } else {
      return this.branches[this.TWO_OR_MORE].query(bestTarget);
    }
  };

  node.calculateBestInterferenceTarget = function () {
    let allEnemies = this.battleState.getEnemiesOfUnit(this.actor);
    // heroes only CC heroes in PvP
    if (this.actor.hero && !this.battleState.isCaverns) {
      allEnemies = allEnemies.filter(({ hero, poppet }) => hero && !poppet);
    }

    if (allEnemies.length === 0) {
      return null;
    }

    const fewestConditions = this.calculateFewestConditions(allEnemies);
    const candidates = this.calculateEnemiesWithFewestConditions(
      allEnemies,
      fewestConditions
    );
    return this.calculateClosestCandidate(candidates) || allEnemies[0];
  };

  node.getNumCCConditions = function (unit) {
    if (!unit) return 99;

    let conditionCount = 0;
    for (const c of Object.values(unit.conditions))
      if (CC_CONDITIONS.includes(c.handle)) ++conditionCount;

    return conditionCount;
  };

  node.calculateFewestConditions = function (enemies) {
    for (let i = 0; i < 5; ++i) {
      for (const enemy of enemies) {
        if (this.getNumCCConditions(enemy) === i) {
          return i;
        }
      }
    }
  };

  node.calculateEnemiesWithFewestConditions = function (
    enemies,
    fewestConditions
  ) {
    const candidates = [];

    for (const enemy of enemies) {
      if (node.getNumCCConditions(enemy) <= fewestConditions) {
        candidates.push(enemy);
      }
    }

    return candidates;
  };

  node.calculateClosestCandidate = function (candidates) {
    if (candidates.length === 0) {
      return null;
    }

    let closest = candidates[0];
    let bestDist = BattleCalc.boardDistance(this.actor, closest);

    for (const enemy of candidates) {
      const dist = BattleCalc.boardDistance(actor, enemy);
      if (dist < bestDist) {
        closest = enemy;
        bestDist = dist;
      }
    }

    return closest;
  };

  return node;
};
module.exports = InterferenceDecision;
